import { useRef } from "react";
import { useRouter } from "next/router";
import { Bar, getElementAtEvent } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale } from "chart.js/auto";
import Annotation from "chartjs-plugin-annotation";
import styled from "styled-components";
import {
  ColorAA,
  getColorsForDataset,
  useAATheme,
} from "@bagel-web/components";
import { SessionRange, SessionTreeNode } from "../types";
import {
  BAR_GAP_PADDING,
  EMPTY_SESSION_WIDTH,
  getDefaultChartOptions,
} from "../reports/config/cloneTimeline";

ChartJS.register(CategoryScale);
ChartJS.register(Annotation);

type TimelineSession = {
  id: string;
  start?: number | null;
  end?: number | null;
};

export const TimelineContainer = styled.div`
  width: 100%;
  padding: 10px 10px 10px 10px;
  background-color: var(--aa-surface-bg);
  border-radius: 6px;
`;

const CloneTimeline = ({
  sessionId,
  sessionParents,
  sessionChildren,
}: {
  sessionId: string;
  sessionParents?: SessionRange[] | null;
  sessionChildren?: SessionTreeNode | null;
}) => {
  const chartRef = useRef();

  // Place ancestor sessions in separate datasets to enable bar stacking.
  const ancestorSessions: Array<TimelineSession> = [];
  const sessions: Array<TimelineSession> = [];

  ancestorSessions.push(
    ...(sessionParents
      ?.filter((parent) => parent.session_id !== sessionId)
      ?.map((parent, index) => {
        let start = null;
        // Increase bar width by 1 to eliminate gaps between ancestors.
        if (parent.first_moment_id) {
          start =
            parent.first_moment_id -
            (index > 0 && index < sessionParents.length - 1
              ? BAR_GAP_PADDING
              : 0);
        }
        const end = parent.last_moment_id;

        return {
          id: parent.session_id,
          start,
          end,
        };
      }) || [])
  );

  const currentSession = sessionParents?.find(
    (session) => session.session_id === sessionId
  );

  const currentSessionStart =
    sessionChildren?.origin_moment_id || currentSession?.first_moment_id || 1;
  // Adding EMPTY_SESSION_WIDTH to end ensures that the session is still visible when the
  // first and last moment are the same.
  let currentSessionEnd =
    sessionChildren?.last_moment_id ||
    currentSessionStart + EMPTY_SESSION_WIDTH ||
    1 + EMPTY_SESSION_WIDTH;
  if (currentSessionEnd === currentSessionStart) {
    currentSessionEnd += EMPTY_SESSION_WIDTH;
  }

  sessions.push({
    id: sessionId,
    start: currentSessionStart,
    end: currentSessionEnd,
  });

  const addChildSessions = (children?: Array<SessionTreeNode>) => {
    children?.forEach((child) => {
      if (child.origin_moment_id && child.last_moment_id) {
        const start = child.origin_moment_id || 0;
        const end =
          (child.last_moment_id || 0) +
          (child.last_moment_id === child.origin_moment_id
            ? EMPTY_SESSION_WIDTH
            : 0);

        sessions.push({ id: child.session_id, start, end });
      }

      if (child.children) {
        addChildSessions(child.children);
      }
    });
  };

  addChildSessions(sessionChildren?.children);

  if (sessions.length === 0 && ancestorSessions.length === 0) {
    return null;
  }

  const router = useRouter();
  const handleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (chartRef.current) {
      const element = getElementAtEvent(chartRef.current, e);
      const index = element?.[0]?.index;
      const datasetIndex = element?.[0]?.datasetIndex;

      if (datasetIndex === 0 && index !== undefined && sessions[index]) {
        router.push(`/sessions/${sessions[index].id}/cloned-sessions`);
      } else {
        const ancestorIndex = datasetIndex ? datasetIndex - 1 : undefined;
        if (ancestorIndex !== undefined && ancestorSessions[ancestorIndex]) {
          router.push(
            `/sessions/${ancestorSessions[ancestorIndex].id}/cloned-sessions`
          );
        }
      }
    }
  };

  const { isDarkMode } = useAATheme();
  const ancestorStyles = getColorsForDataset(3, isDarkMode);
  const currentSessionStyles = getColorsForDataset(0, isDarkMode);
  const childStyles = getColorsForDataset(1, isDarkMode);

  const options = getDefaultChartOptions({ isDarkMode });
  const legendTextColor = isDarkMode ? ColorAA.lightBeige : ColorAA.lightBlack;
  options.plugins!.legend!.labels!.generateLabels = () => {
    return [
      ...(ancestorSessions.length > 0
        ? [
            {
              text: "Ancestor Sessions",
              fillStyle: ancestorStyles.backgroundColor,
              strokeStyle: ancestorStyles.borderColor,
              fontColor: legendTextColor,
              font: { size: 14 },
            },
          ]
        : []),
      {
        text: "Current Session",
        fillStyle: currentSessionStyles.backgroundColor,
        strokeStyle: currentSessionStyles.borderColor,
        fontColor: legendTextColor,
        font: { size: 14 },
      },
      ...(sessions.length > 1
        ? [
            {
              text: "Child Sessions",
              fillStyle: childStyles.backgroundColor,
              strokeStyle: childStyles.borderColor,
              fontColor: legendTextColor,
              font: { size: 14 },
            },
          ]
        : []),
    ];
  };
  options.plugins!.tooltip!.callbacks!.title = (context) => {
    if (
      context[0]?.datasetIndex !== undefined &&
      context[0]?.dataset.label === "ancestor"
    ) {
      const ancestorIndex = context[0].datasetIndex - 1;
      return ancestorIndex >= 0
        ? ancestorSessions[ancestorIndex]?.id
        : undefined;
    }
  };

  return (
    <TimelineContainer style={{ height: `${sessions.length * 30 + 100}px` }}>
      <Bar
        ref={chartRef}
        onClick={handleClick}
        data={{
          labels: sessions.map((session) => session.id),
          datasets: [
            // Current session and child sessions in one dataset to not stack
            {
              data: sessions.map((session) => [session.start, session.end]),
              borderColor: sessions.map((_, index) =>
                index === 0
                  ? currentSessionStyles.borderColor
                  : childStyles.borderColor
              ),
              backgroundColor: sessions.map((_, index) =>
                index === 0
                  ? currentSessionStyles.backgroundColor
                  : childStyles.backgroundColor
              ),
              borderWidth: 2,
              borderSkipped: false,
              label: "sessions",
            },
            // Ancestors in separate datasets each to stack
            ...ancestorSessions.map((session) => ({
              data: [[session.start, session.end]],
              borderColor: ancestorStyles.borderColor,
              backgroundColor: ancestorStyles.backgroundColor,
              borderWidth: 2,
              borderSkipped: false,
              label: "ancestor",
            })),
          ],
        }}
        options={options}
      />
    </TimelineContainer>
  );
};

export default CloneTimeline;
